import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpError } from 'src/app/core/http-error';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { AttributeValue } from '../models/attribute-value';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class AttributesValuesService extends CrudService {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<AttributeValue>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/Attributes/${context.value}/AttributeValue`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<AttributeValue>(AttributeValue, x);
                return res;
            })
        );
    }

    Export(attributeId: number): Observable<Blob> {
        // Appel de la Web Api
        return this.http
            .get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/Attributes/${attributeId}/AttributeValue/export`, {
                responseType: 'blob',
            })
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    update(attributeId: number, attributeValueId: number, attributeValue: AttributeValue): Observable<AttributeValue> {
        // Appel de la Web Api
        return this.http
            .put<AttributeValue>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/Attributes/${attributeId}/AttributeValue/${attributeValueId}`,
                attributeValue,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    delete(attributeId: number, attributeValueId: number): Observable<boolean> {
        // Appel de la Web Api
        return this.http
            .delete<AttributeValue>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/Attributes/${attributeId}/AttributeValue/${attributeValueId}`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    add(attributeId: number, attributeValue: AttributeValue): Observable<AttributeValue> {
        // Appel de la Web Api
        return this.http
            .post<AttributeValue>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/Attributes/${attributeId}/AttributeValue`,
                attributeValue,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    get(attributeId: number, attributeValueId: string): Observable<AttributeValue> {
        // Appel de la Web Api
        return this.http
            .get<AttributeValue>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/Attributes/${attributeId}/AttributeValue/${attributeValueId}`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    public applyBatchModifications(attributeId: number, modifications: BatchModification<AttributeValue>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        modifications.deletes.forEach((x) => {
            x.data.attributeId = attributeId as number;
            requests.push(
                this.delete(x.data.attributeId, x.data.id as number).pipe(
                    map((y) => new BatchResult(x.data.id, true)),
                    catchError((err: HttpError) => of(new BatchResult(x.data.id, false, `La valeur \`${x.data.name}\` n\'a pas pu être supprimée.`, err)))
                )
            );
        });

        modifications.updates.forEach((x) => {
            x.data.attributeId = attributeId as number;
            requests.push(
                this.update(x.data.attributeId, x.data.id, x.data).pipe(
                    map((y) => new BatchResult(x.data.editableId, true)),
                    catchError((err: HttpError) => of(new BatchResult(x.data.id, false, `La valeur \`${x.data.name}\` n\'a pas pu être modifée.`, err)))
                )
            );
        });

        modifications.creates.forEach((x) => {
            x.data.attributeId = attributeId as number;
            requests.push(
                this.add(x.data.attributeId, x.data).pipe(
                    map((y) => new BatchResult(x.data.id, true)),
                    catchError((err: HttpError) => of(new BatchResult(x.data.id, false, `La valeur \`${x.data.name}\` n\'a pas pu être créée`, err)))
                )
            );
        });

        return forkJoin(requests);
    }
}
